import MaskTemplate from './html/modal-mask.html';
import MaskTemplateCss from './html/css/modal-mask.css';
import { stopEvent, hideElement, getWinWidth, showElement, isEmpty, isString, getElementAs } from '../modules/utils';
export const ERROR_NULL_CONTENTS = new Error('Contents must not be NULL');
export const templateRegEx = /\${(.+?)}/g;
const docLoadPromise = (resolve) => {
    if (!isEmpty(document.body)) {
        resolve();
        return;
    }
    setTimeout(docLoadPromise, 111, resolve);
};
const firstChild = (div) => {
    const items = Array.prototype.slice.call(div.children);
    for (let count = 0; count < items.length; count++) {
        if (items[count].nodeType === Node.ELEMENT_NODE) {
            return items[count];
        }
    }
    return null;
};
export const createDialogContainer = (html, css) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    if (!isEmpty(css)) {
        css.forEach(descriptor => {
            let style = null;
            if (!isEmpty(descriptor.id)) {
                style = div.querySelector(`#${descriptor.id}`);
                if (style !== null) {
                    div.removeChild(style);
                    style = null;
                }
            }
            docLoadPromise(() => {
                style = document.createElement('style');
                style.id = descriptor.id;
                style.setAttribute('id', descriptor.id);
                style.textContent = descriptor.cssContent;
                document.body.appendChild(style);
            });
        });
    }
    const dialog = firstChild(div);
    if (dialog === null) {
        throw ERROR_NULL_CONTENTS;
    }
    docLoadPromise(() => {
        document.body.appendChild(dialog);
    });
    return dialog;
};
export class CustomizableDialog {
    constructor() {
        this.processTemplateText = (templatedText, uiConfig) => {
            if (templatedText.search(templateRegEx) > -1) {
                let processedValue = templatedText;
                let matchResult = templateRegEx.exec(templatedText);
                while (matchResult !== null) {
                    const templateID = matchResult[1];
                    if (templateID !== undefined) {
                        const [itemID, defaultDescriptor] = templateID.split(/,\s*default\:\s*/);
                        const value = uiConfig[itemID.trim()];
                        if (isString(value) && !isEmpty(value.trim())) {
                            processedValue = processedValue.replace(matchResult[0], value.trim());
                        }
                        else if (defaultDescriptor !== undefined) {
                            processedValue = processedValue.replace(matchResult[0], defaultDescriptor);
                        }
                        else {
                            processedValue = processedValue.replace(matchResult[0], '');
                        }
                    }
                    matchResult = templateRegEx.exec(templatedText);
                }
                return processedValue;
            }
            return templatedText;
        };
        this.applyAttributes = (customAttributes, uiConfig, el) => {
            const attrCollection = customAttributes.split(';');
            attrCollection.forEach(attrDescriptor => {
                const [attrName, attrText] = attrDescriptor.split(':');
                if (attrName !== undefined && attrText !== undefined) {
                    const attributeName = attrName.trim();
                    const value = this.processTemplateText(attrText, uiConfig);
                    const existingValue = el.getAttribute(attributeName);
                    el.setAttribute(attributeName, value || existingValue || '');
                }
            });
        };
        this.customizeSingleElement = (el, uiConfig) => {
            const data = el.dataset;
            if (!isEmpty(data)) {
                if ('customText' in data) {
                    const textTemplate = data['customText'];
                    const textContents = this.processTemplateText(textTemplate, uiConfig);
                    el.innerText = textContents || el.innerText;
                }
                if ('customHtml' in data) {
                    const htmlTemplate = data['customHtml'];
                    const textContents = this.processTemplateText(htmlTemplate, uiConfig);
                    el.innerHTML = textContents || el.innerHTML;
                }
                if ('attributes' in data) {
                    const customAttributes = data['attributes'];
                    this.applyAttributes(customAttributes, uiConfig, el);
                }
            }
        };
        this.processUIElement = (el, uiConfig) => {
            this.customizeSingleElement(el, uiConfig);
            if (el.nodeType === Node.ELEMENT_NODE) {
                const children = el.children !== undefined ? el.children : el.childNodes;
                Array.prototype.slice.call(children).forEach((child) => {
                    if (child.nodeType === Node.ELEMENT_NODE) {
                        this.processUIElement(child, uiConfig);
                    }
                });
            }
        };
        this.setImageRoot = (contents, imageRootPath) => {
            if (contents === null) {
                throw ERROR_NULL_CONTENTS;
            }
            const path = imageRootPath.endsWith('/') ? imageRootPath.substr(0, imageRootPath.length - 1) : imageRootPath;
            const images = this.getImageElements(contents);
            console.log('Received list of images: ', images, 'path is: ', path);
            images.forEach(elem => {
                var _a;
                const src = (_a = elem.getAttribute('src')) !== null && _a !== void 0 ? _a : elem.src;
                if (isString(src) && src.startsWith('/')) {
                    elem.src = path + src;
                }
            });
        };
        this.getImageElements = (contents) => {
            const images = [];
            if (contents !== null) {
                Array.prototype.slice.call(contents.childNodes).forEach((elem) => this.retrieveImages(elem, images));
            }
            return images;
        };
    }
    retrieveImages(rootElem, images) {
        if (rootElem instanceof HTMLImageElement) {
            images.push(rootElem);
        }
        else {
            Array.prototype.slice.call(rootElem.childNodes).forEach((childNode) => this.retrieveImages(childNode, images));
        }
    }
}
export class SimpleDialog extends CustomizableDialog {
    constructor(html, options) {
        super();
        this.mask = null;
        this.dialog = null;
        this.visibility = false;
        this.uiConfig = null;
        this.options = {
            enableOverlay: true,
            dismissOnEscape: true,
            dismissOnMaskClick: true
        };
        this.isVisible = () => this.visibility;
        let defaultMask = {
            id: 'LL_modal_mask', htmlContent: MaskTemplate, css: [{ cssContent: MaskTemplateCss, id: 'modalMaskCss' }]
        };
        if (typeof (options === null || options === void 0 ? void 0 : options.dismissOnEscape) === 'boolean') {
            this.options.dismissOnEscape = options.dismissOnEscape;
        }
        if (typeof (options === null || options === void 0 ? void 0 : options.dismissOnMaskClick) === 'boolean') {
            this.options.dismissOnMaskClick = options.dismissOnMaskClick;
        }
        if (typeof (options === null || options === void 0 ? void 0 : options.enableOverlay) === 'boolean') {
            this.options.enableOverlay = options.enableOverlay;
        }
        if ((options === null || options === void 0 ? void 0 : options.maskControl) !== undefined && this.options.enableOverlay) {
            defaultMask = options.maskControl;
        }
        this.mask = null;
        this.dialog = null;
        if (this.options.enableOverlay === true) {
            if (isString(defaultMask.id)) {
                this.mask = getElementAs(defaultMask.id);
            }
            if (this.mask === null) {
                this.mask = createDialogContainer(defaultMask.htmlContent, defaultMask.css);
            }
        }
        if (isString(html.id)) {
            this.dialog = getElementAs(html.id);
        }
        if (this.dialog === null) {
            this.dialog = createDialogContainer(html.htmlContent, html.css);
        }
        this.escapeListener = this.escapeListener.bind(this);
        this.hideListener = this.hideListener.bind(this);
        this.reset();
    }
    attachCloseControl(element) {
        element.addEventListener('click', this.hideListener, false);
    }
    reset() {
        this.hide();
    }
    configureUI(uiConfig) {
        this.uiConfig = uiConfig;
        if (this.dialog === null) {
            throw ERROR_NULL_CONTENTS;
        }
        this.processUIElement(this.dialog, this.uiConfig);
    }
    center() {
        if (this.dialog === null) {
            throw ERROR_NULL_CONTENTS;
        }
        const element = this.dialog;
        const wndWidth = getWinWidth();
        const objWidth = element.offsetWidth || 0;
        element.style.left = `${(wndWidth - objWidth) / 2 - 20}px`;
    }
    display() {
        var _a;
        if (this.visibility) {
            return;
        }
        if (this.dialog === null) {
            throw ERROR_NULL_CONTENTS;
        }
        if (this.options.enableOverlay === true && this.mask !== null) {
            showElement(this.mask);
            if (this.options.dismissOnMaskClick === true) {
                this.mask.addEventListener('click', this.hideListener, false);
            }
        }
        if (this.options.dismissOnEscape === true) {
            window.addEventListener('keydown', this.escapeListener, false);
        }
        showElement(this.dialog);
        if (!isEmpty((_a = this.dialog) === null || _a === void 0 ? void 0 : _a.offsetWidth)) {
            this.visibility = true;
            this.center();
        }
        else {
            hideElement(this.dialog);
            docLoadPromise(() => {
                showElement(this.dialog);
                this.center.bind(this);
                this.visibility = true;
            });
        }
    }
    hide() {
        if (this.dialog === null) {
            throw ERROR_NULL_CONTENTS;
        }
        if (this.options.enableOverlay === true && this.mask !== null) {
            hideElement(this.mask);
            this.mask.removeEventListener('click', this.hideListener, false);
        }
        hideElement(this.dialog);
        this.visibility = false;
        window.removeEventListener('keydown', this.escapeListener, false);
    }
    getContent() {
        return this.dialog;
    }
    getMask() {
        return this.mask;
    }
    hideListener(e) {
        stopEvent(e);
        this.hide();
        return false;
    }
    escapeListener(e) {
        const keyCode = e.keyCode || e.which;
        const code = e.code;
        if (keyCode !== 27 && code !== 'Escape') {
            return;
        }
        this.hideListener(e);
    }
}
